import { useEffect, useMemo, useRef, useState } from 'react';
import { useApp } from '../../state/AppState';
import { useI18n, type I18nKey, type I18nVars } from '../../i18n';
import { localdb } from '../../lib/localdb';
import { chatKey, sanitiseEntries } from '../../state/allyChats';

/**
 * Every Astrally AI conversation, as a drawer over the chat.
 *
 * A drawer rather than a screen on purpose: the chat behind it keeps its
 * scroll position and its in-flight generation, so switching threads costs a
 * tap and nothing else. Nothing in here spends a message — titles, times and
 * previews all come off the device.
 *
 * Rename and delete confirm INSIDE the panel. A native confirm() renders
 * unstyled in the Android WebView and reads like a browser error, so the
 * destructive step is the app's own UI: a row that turns into the question.
 */

/** The `t` from useI18n, taken as an argument so this stays a pure function. */
type Translate = (key: I18nKey, vars?: I18nVars) => string;

/** Human time for a list row. Deliberately coarse — this is a glance, not a log. */
export function relativeTime(iso: string, t: Translate, now: number = Date.now()): string {
  const then = new Date(iso).getTime();
  if (!Number.isFinite(then)) return '';
  const mins = Math.floor(Math.max(0, now - then) / 60000);
  if (mins < 1) return t('chats.time.now');
  if (mins < 60) return t('chats.time.minutes', { n: mins });
  const hours = Math.floor(mins / 60);
  if (hours < 24) return t('chats.time.hours', { n: hours });
  const days = Math.floor(hours / 24);
  if (days === 1) return t('chats.time.yesterday');
  if (days < 7) return t('chats.time.days', { n: days });
  return new Date(then).toLocaleDateString(undefined, { day: 'numeric', month: 'short' });
}

export interface ChatListProps {
  open: boolean;
  onClose(): void;
}

export function ChatList({ open, onClose }: ChatListProps) {
  const { t } = useI18n();
  const app = useApp();
  const panelRef = useRef<HTMLDivElement>(null);
  const renameRef = useRef<HTMLInputElement>(null);
  const [previews, setPreviews] = useState<Record<string, string>>({});
  const [renaming, setRenaming] = useState<string | null>(null);
  const [draftTitle, setDraftTitle] = useState('');
  const [confirming, setConfirming] = useState<string | null>(null);

  const chats = app.chats;
  const uid = app.uid;

  // Previews are read only while the drawer is open, and only for the rows a
  // person could plausibly scroll to. All of it is local IndexedDB — no call,
  // no cost — but there is no reason to read 200 threads to show 30 rows.
  useEffect(() => {
    if (!open || !uid) return;
    let cancelled = false;
    void (async () => {
      const next: Record<string, string> = {};
      for (const chat of chats.slice(0, 30)) {
        const entries = sanitiseEntries(
          await localdb.get<unknown>('kv', chatKey(uid, chat.id)).catch(() => undefined),
        );
        const last = entries[entries.length - 1];
        if (last) {
          next[chat.id] = `${last.role === 'user' ? t('chats.youPrefix') : ''}${last.text
            .replace(/\s+/g, ' ')
            .trim()
            .slice(0, 120)}`;
        }
      }
      if (!cancelled) setPreviews(next);
    })();
    return () => {
      cancelled = true;
    };
  }, [open, uid, chats, t]);

  // Escape closes whatever is innermost: the confirm, then the rename, then
  // the drawer itself.
  useEffect(() => {
    if (!open) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key !== 'Escape') return;
      e.stopPropagation();
      if (confirming) setConfirming(null);
      else if (renaming) setRenaming(null);
      else onClose();
    };
    document.addEventListener('keydown', onKey);
    panelRef.current?.focus();
    return () => document.removeEventListener('keydown', onKey);
  }, [open, onClose, confirming, renaming]);

  useEffect(() => {
    if (renaming) renameRef.current?.focus();
  }, [renaming]);

  // Reset the transient row states each time the drawer opens.
  useEffect(() => {
    if (!open) {
      setRenaming(null);
      setConfirming(null);
    }
  }, [open]);

  const now = useMemo(() => Date.now(), [open]); // eslint-disable-line react-hooks/exhaustive-deps

  if (!open) return null;

  const startNew = async () => {
    await app.newChat();
    onClose();
  };

  const pick = async (id: string) => {
    if (id !== app.activeChatId) await app.openChat(id);
    onClose();
  };

  const saveRename = async (id: string) => {
    const clean = draftTitle.trim();
    // An empty box is not a rename — leave the title alone rather than
    // replacing it with nothing.
    if (clean) await app.renameChat(id, clean);
    setRenaming(null);
  };

  return (
    <div
      style={{
        position: 'fixed',
        inset: 0,
        zIndex: 96,
        background: 'var(--scrim)',
        display: 'flex',
      }}
      onClick={onClose}
    >
      <div
        ref={panelRef}
        role="dialog"
        aria-modal="true"
        aria-label={t('chats.aria')}
        tabIndex={-1}
        className="card card--strong fade-in"
        style={{
          width: 'min(380px, 88vw)',
          height: '100%',
          display: 'flex',
          flexDirection: 'column',
          gap: 10,
          borderRadius: 0,
          borderTop: 'none',
          borderBottom: 'none',
          borderLeft: 'none',
          outline: 'none',
          overflowY: 'auto',
          padding:
            'calc(16px + var(--safe-top)) 16px calc(16px + var(--safe-bottom)) calc(16px + var(--safe-left))',
        }}
        onClick={(e) => e.stopPropagation()}
      >
        <div className="spread">
          <div>
            <div className="h2">{t('chats.title')}</div>
            <div className="tiny faint">
              {chats.length === 0
                ? t('chats.none')
                : chats.length === 1
                  ? t('chats.countOne')
                  : t('chats.countMany', { n: chats.length })}
            </div>
          </div>
          <button className="btn btn--ghost btn--small" aria-label={t('common.close')} onClick={onClose}>
            <span aria-hidden>✕</span>
          </button>
        </div>

        <button className="btn btn--primary btn--block" onClick={startNew}>
          <span aria-hidden>＋ </span>
          {t('chats.new')}
        </button>

        {chats.length === 0 ? (
          <div className="muted small" style={{ lineHeight: 1.55, marginTop: 6 }}>
            {t('chats.empty')}
          </div>
        ) : (
          <div className="col" style={{ gap: 0 }}>
            {chats.map((chat) => {
              const active = chat.id === app.activeChatId;
              if (renaming === chat.id) {
                return (
                  <div key={chat.id} className="col" style={{ gap: 8, padding: '12px 4px' }}>
                    <input
                      ref={renameRef}
                      className="input"
                      value={draftTitle}
                      maxLength={60}
                      aria-label={t('chats.nameAria')}
                      onChange={(e) => setDraftTitle(e.target.value)}
                      onKeyDown={(e) => {
                        if (e.key === 'Enter') void saveRename(chat.id);
                      }}
                    />
                    <div className="row">
                      <button className="chip" onClick={() => setRenaming(null)}>
                        {t('common.cancel')}
                      </button>
                      <button className="chip chip--on" onClick={() => void saveRename(chat.id)}>
                        {t('common.save')}
                      </button>
                    </div>
                  </div>
                );
              }

              if (confirming === chat.id) {
                return (
                  <div key={chat.id} className="col" style={{ gap: 8, padding: '12px 4px' }}>
                    <div className="banner banner--danger small">
                      {t('chats.deleteConfirm', { title: chat.title })}
                    </div>
                    <div className="row">
                      <button className="chip" onClick={() => setConfirming(null)}>
                        {t('chats.keep')}
                      </button>
                      <button
                        className="chip danger"
                        onClick={async () => {
                          setConfirming(null);
                          await app.deleteChat(chat.id);
                        }}
                      >
                        {t('chats.delete')}
                      </button>
                    </div>
                  </div>
                );
              }

              return (
                <div
                  key={chat.id}
                  className="list-item"
                  style={{ alignItems: 'flex-start', gap: 8, cursor: 'default' }}
                >
                  <button
                    onClick={() => void pick(chat.id)}
                    aria-current={active ? 'true' : undefined}
                    style={{
                      flex: 1,
                      minWidth: 0,
                      textAlign: 'left',
                      background: 'transparent',
                      border: 'none',
                      padding: 0,
                      font: 'inherit',
                      color: 'inherit',
                      cursor: 'pointer',
                    }}
                  >
                    <div
                      className="small"
                      style={{
                        fontWeight: 600,
                        color: active ? 'var(--accent-ink)' : 'var(--text)',
                        overflow: 'hidden',
                        textOverflow: 'ellipsis',
                        whiteSpace: 'nowrap',
                      }}
                    >
                      {chat.title}
                    </div>
                    <div className="tiny faint" style={{ marginTop: 2 }}>
                      {relativeTime(chat.updatedAtISO, t, now)}
                      {' · '}
                      {chat.messageCount === 0
                        ? t('chats.rowEmpty')
                        : chat.messageCount === 1
                          ? t('chats.messagesOne')
                          : t('chats.messagesMany', { n: chat.messageCount })}
                    </div>
                    {previews[chat.id] && (
                      <div
                        className="tiny muted"
                        style={{
                          marginTop: 4,
                          overflow: 'hidden',
                          textOverflow: 'ellipsis',
                          whiteSpace: 'nowrap',
                        }}
                      >
                        {previews[chat.id]}
                      </div>
                    )}
                  </button>
                  <div className="col" style={{ gap: 4, flexShrink: 0 }}>
                    <button
                      className="chip"
                      style={{ minHeight: 34, padding: '2px 10px' }}
                      onClick={() => {
                        setDraftTitle(chat.title);
                        setConfirming(null);
                        setRenaming(chat.id);
                      }}
                    >
                      {t('chats.rename')}
                    </button>
                    <button
                      className="chip danger"
                      style={{ minHeight: 34, padding: '2px 10px' }}
                      onClick={() => {
                        setRenaming(null);
                        setConfirming(chat.id);
                      }}
                    >
                      {t('memory.delete')}
                    </button>
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
}
